import { useEditContext } from '@components/utils/contexts/EditContext';
import styled from '@emotion/styled';
import {
    StoryPreviewFragment,
    StoryToPlayFragment,
} from '@graphql/generated/graphql';
import { FC, useMemo } from 'react';

import { CurrentStoryPageWrapper } from './components/layout/CurrentStoryPageWrapper';
import { NavigationProvider } from './contexts/NavigationContext';
import { PageProvider } from './contexts/PageContext';
import { ProgressProvider } from './contexts/ProgressContext';
import { RatingProvider } from './contexts/RatingContext';
import { VideoPlayerProvider } from './contexts/VideoPlayerContext';
import { StoryPageDisplay } from './enums';
import { Renderer, RendererWithTester, SanitizedStoryPage } from './types';
import { getRenderer } from './utils/getRenderer';
import { getSanitizedStoryPages } from './utils/getSanitizedStoryPages';

interface Props {
    story: StoryToPlayFragment;
    storyPages?: SanitizedStoryPage[];
    previousStory?: StoryPreviewFragment;
    nextStory?: StoryPreviewFragment;
    pageIndex?: number;
    module?: string;
    storyPageDisplay?: StoryPageDisplay;
    renderers: RendererWithTester[];
}

interface StoryPageWithRenderer extends SanitizedStoryPage {
    renderer: Renderer;
}

const Background = styled.div`
    width: 100%;
    height: 100%;
    background: ${(props) => props.theme.palette.background.default};
`;

const Story: FC<Props> = ({
    story,
    previousStory,
    nextStory,
    pageIndex,
    module,
    renderers,
    storyPageDisplay = StoryPageDisplay.Default,
    storyPages = getSanitizedStoryPages(story),
}) => {
    const { isEditMode } = useEditContext();

    const _storyPages = useMemo(() => {
        if (isEditMode) {
            return storyPages.map((sp) => ({ ...sp, looping: true }));
        }

        return storyPages;
    }, [isEditMode, storyPages]);

    const pageWithRenderers = useMemo<StoryPageWithRenderer[]>(
        () =>
            _storyPages.map((storyPage) => ({
                ...storyPage,
                renderer: getRenderer({
                    renderers,
                    storyPage,
                    storyPageDisplay,
                }),
            })),
        [_storyPages, storyPageDisplay, renderers]
    );

    return (
        <NavigationProvider
            module={module}
            story={story}
            storyPages={_storyPages}
            previousStory={previousStory}
            nextStory={nextStory}
        >
            <ProgressProvider
                numberOfPages={_storyPages.length}
                pageIndex={pageIndex}
            >
                <RatingProvider story={story}>
                    <Background>
                        {pageWithRenderers.map(
                            ({ renderer: Renderer, ...storyPage }, index) => (
                                <PageProvider key={index} pageIndex={index}>
                                    <VideoPlayerProvider storyPage={storyPage}>
                                        <CurrentStoryPageWrapper
                                            pageIndex={index}
                                        >
                                            <Renderer
                                                story={story}
                                                storyPage={storyPage}
                                            />
                                        </CurrentStoryPageWrapper>
                                    </VideoPlayerProvider>
                                </PageProvider>
                            )
                        )}
                    </Background>
                </RatingProvider>
            </ProgressProvider>
        </NavigationProvider>
    );
};

export default Story;
